import { FC, PropsWithChildren, ReactElement, useCallback } from 'react';

import { useGameConfig } from '../../config/GameConfigProvider';
import { GtagLogger } from '../../logger/GtagLogger';
import { Logger } from '../../logger/Logger';
import { L10nText } from '../../services/uiTranslations/reactBindings/L10nText';
import { useCoreDispatch } from '../../store/CoreStore';
import { useConfigSlice } from '../../store/slices/config/selectors';
import { useHeaderSlice } from '../../store/slices/header/selectors';
import { headerActions } from '../../store/slices/header/slice';
import { useNickNameInfoSlice } from '../../store/slices/nickname/selectors';
import { nickNameInfoActions } from '../../store/slices/nickname/slice';
import { useMasterVolumeControl } from '../../store/slices/settings/hooks/useVolumeControl';
import { useSettingsSlice } from '../../store/slices/settings/selectors';
import { settingsActions } from '../../store/slices/settings/slice';
import { useSubtitlesSlice } from '../../store/slices/subtitles/selectors';
import { subtitlesActions } from '../../store/slices/subtitles/slice';
import { SubtitlesGroup } from '../../store/slices/subtitles/types';
import { Toggle } from '../controls/toggle/Toggle';
import { VolumeSlider } from '../controls/volumeSlider/VolumeSlider';
import { ErrorBoundary } from '../errorBoundary/ErrorBoundary';
import { Icons } from '../icons/iconNames';
import { Loader } from '../loader/Loader';
import { ModalContainer } from '../modal/modalContainer/ModalContainer';
import { ModalSection } from '../modal/modalSection/ModalSection';
import type { TabProps } from '../tabs/Tabs';
import { Tabs } from '../tabs/Tabs';

import { NickNameEditFormConnected } from './nickName/NickNameEditFormConnected';
import { NickNameSettingsConnected } from './nickName/NickNameSettingsConnected';
import { SettingsChatConnected } from './SettingsChatConnected';
import { SettingsSubtitlesConnected } from './SettingsSubtitlesConnected';
import { SettingsVideoConnected } from './SettingsVideoConnected';
import { VolumeChannelControlConnected } from './VolumeChannelControlConnected';

import styles from './SettingsConnected.module.css';

interface SettingsWrapperProps extends PropsWithChildren {
  children: ReactElement;
  gameSpecificTab?: TabProps[];
  selectedTabIndex: number;
  tabToHide?: string[];
}

const SettingsWrapper: FC<SettingsWrapperProps> = ({
  children,
  gameSpecificTab,
  selectedTabIndex,
  tabToHide,
}): ReactElement => {
  const generalTab: TabProps = {
    content: children,
    label: <L10nText tk="GENERAL" />,
  };

  return gameSpecificTab ? (
    <Tabs
      isOneWidth
      renderInactive
      initActiveTab={selectedTabIndex}
      tabs={[generalTab, ...gameSpecificTab.filter((tab) => !tabToHide?.includes(tab.label.props.tk))]}
    />
  ) : (
    children
  );
};

export const SettingsConnected: FC = (): ReactElement => {
  const dispatch = useCoreDispatch();
  const masterVolumeControl = useMasterVolumeControl();
  const { soundControls, gameSettingsSpecificTab, config } = useGameConfig();
  const settingsTabToHide = useSettingsSlice.getSettingsTabToHide();

  const isNickNameEditOpen = useNickNameInfoSlice.getIsNickNameEditOpen();
  const restrictNicknameEdit = useConfigSlice.isRestrictNickNameEdit();
  const isStreamEnabled = useSettingsSlice.getIsStreamEnabled();
  const noVolumeControl = useSettingsSlice.getNoVolumeControl();
  const isChatDisabled = useConfigSlice.isChatDisabled();
  const isPrivateTable = useConfigSlice.isPrivateTable();
  const hasVideoLocation = useConfigSlice.hasVideoLocation();

  const playerLanguage = useConfigSlice.playerLanguage();
  const isSubtitlesAvailable = useSubtitlesSlice.isAvailable();
  const isSubtitlesEnabled = useSubtitlesSlice.isEnabled();
  const subtitlesGroup = useSubtitlesSlice.group();
  const navRouteLevel = useHeaderSlice.getNavRouteLevel();
  const isChatDisabledForJurisdiction = useConfigSlice.getIsChatDisabledForJurisdiction();
  const disableStudioSound = useConfigSlice.compliance()?.disableStudioSound;
  const isCrashGame = config?.isCrashGame;
  const settingsTabIndex = useHeaderSlice.getSettingsTabIndex();

  const subtitlesSectionTitleKey =
    subtitlesGroup === SubtitlesGroup.GROUP3 ? 'SETTINGS_SUBTITLES_ENGLISH' : 'SETTINGS_SUBTITLES';

  const handleStreamToggle = useCallback(
    (checked: boolean): void => {
      Logger.logEvent('settingsDisableStream');
      dispatch(settingsActions.setStreamEnabled(checked));
    },
    [dispatch]
  );

  const handleSubtitlesToggle = useCallback(
    (checked: boolean): void => {
      dispatch(subtitlesActions.setIsEnabled(checked));
      GtagLogger.logUniqueEvent('subtitles_settings_changed', {
        subtitlesEnabled: checked,
        subtitlesGroup: `sub_enabled_${subtitlesGroup}`,
        subtitlesPlayerLanguage: playerLanguage,
      });
    },
    [dispatch, subtitlesGroup, playerLanguage]
  );

  const handleNickNameSave = useCallback(() => {
    if (navRouteLevel === 1) dispatch(headerActions.popNavRoute());
    dispatch(nickNameInfoActions.setIsNickNameEditOpen(false));
  }, [dispatch, navRouteLevel]);

  return (
    <ErrorBoundary fallback={<Loader />}>
      {isNickNameEditOpen ? (
        <NickNameEditFormConnected onNickNameSave={handleNickNameSave} />
      ) : (
        <SettingsWrapper
          gameSpecificTab={gameSettingsSpecificTab}
          selectedTabIndex={settingsTabIndex}
          tabToHide={settingsTabToHide}
        >
          <ModalContainer>
            {isCrashGame ? null : (
              <ModalSection
                dataTestId="settings-video"
                title={<L10nText tk="VIDEO" />}
                icon={Icons.Video}
                action={
                  hasVideoLocation ? (
                    <Toggle checked={isStreamEnabled} onChange={(event) => handleStreamToggle(event.target.checked)} />
                  ) : undefined
                }
                modalSectionClass={styles.videoSection}
                modalSectionHeaderClass={styles.videoSectionHeader}
              >
                <SettingsVideoConnected />
              </ModalSection>
            )}
            <ModalSection
              dataTestId="settings-sound"
              title={<L10nText tk="SOUNDS" />}
              icon={Icons.SoundOn}
              action={
                masterVolumeControl && !noVolumeControl ? <VolumeSlider control={masterVolumeControl} /> : undefined
              }
              modalSectionClass={styles.soundSection}
              modalSectionHeaderClass={styles.soundSectionHeader}
              modalSectionGridClass={styles.soundSectionGrid}
            >
              {soundControls
                .filter((control) => !(disableStudioSound && control.channel === 'studio'))
                .map((control) => {
                  return (
                    <VolumeChannelControlConnected
                      channel={control.channel}
                      disabledWithStream={control.disabledWithStream}
                      key={control.channel}
                      label={control.label}
                    />
                  );
                })}
            </ModalSection>
            {isSubtitlesAvailable ? (
              <ModalSection
                dataTestId="settings-subtitles"
                title={<L10nText tk={subtitlesSectionTitleKey} />}
                icon={Icons.Subtitles}
                action={
                  <Toggle
                    checked={isSubtitlesEnabled}
                    onChange={(event) => handleSubtitlesToggle(event.target.checked)}
                  />
                }
                modalSectionClass={styles.subtitleSection}
              >
                <SettingsSubtitlesConnected />
              </ModalSection>
            ) : null}
            {!isPrivateTable && (isChatDisabledForJurisdiction || isChatDisabled) ? null : (
              <ModalSection
                dataTestId="settings-chat"
                title={<L10nText tk="CHAT" />}
                icon={Icons.Chat}
                modalSectionClass={styles.chatSection}
              >
                <SettingsChatConnected />
              </ModalSection>
            )}
            {restrictNicknameEdit ? null : (
              <ModalSection
                dataTestId="settings-account"
                title={<L10nText tk="SETTINGS_ACCOUNT" />}
                icon={Icons.Player}
                modalSectionHeaderClass={styles.nickNameSectionHeader}
                modalSectionGridClass={styles.nickNameSectionGrid}
              >
                <NickNameSettingsConnected />
              </ModalSection>
            )}
          </ModalContainer>
        </SettingsWrapper>
      )}
    </ErrorBoundary>
  );
};
